/**
 * Delete confirmation for one player or for a multi-select batch.
 *
 * Both paths share the same dialog because they share the same consequence
 * text: `DELETE /players/v1/{player_id}` is a soft delete, so the row leaves
 * the roster, play entry, participation and export lists while the plays
 * already logged against the player stay in the archive. Saying that out loud
 * is the difference between a confirmation and a speed bump.
 *
 * A batch is deleted one request at a time so a single 403/409 does not hide
 * the rest: the failures are reported inside the dialog and the successes are
 * kept.
 */
import { useState } from 'react';

import { useApiClient } from '../../app/providers';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { ApiError } from '../../lib/api-error';
import type { Player } from '../../lib/api-types';

/** Names listed in full up to this many; beyond it the dialog counts them. */
const NAME_LIST_LIMIT = 8;

function describe(players: readonly Player[]): string {
  const [first] = players;
  if (players.length === 1 && first !== undefined) {
    return `Delete ${first.full_name} (${first.cohort_label ?? first.cohort}) from the roster?`;
  }
  const names = players.slice(0, NAME_LIST_LIMIT).map((player) => player.full_name);
  const overflow = players.length - names.length;
  const list = overflow > 0 ? `${names.join(', ')} and ${overflow} more` : names.join(', ');
  return `Delete ${players.length} players from the roster: ${list}?`;
}

export interface RosterDeleteDialogProps {
  /** One player for the row action, many for the bulk action. */
  players: readonly Player[];
  onCancel: () => void;
  /** Called once every deletion succeeded, with how many rows went away. */
  onDeleted: (removedCount: number) => void;
}

export function RosterDeleteDialog({
  players,
  onCancel,
  onDeleted,
}: RosterDeleteDialogProps): JSX.Element {
  const apiClient = useApiClient();
  const [error, setError] = useState<string | null>(null);

  async function confirm(): Promise<void> {
    setError(null);
    const failures: string[] = [];
    for (const player of players) {
      try {
        await apiClient.removePlayer(player.player_id);
      } catch (caught) {
        const reason = ApiError.isApiError(caught) ? caught.message : 'Unexpected error.';
        failures.push(`${player.full_name}: ${reason}`);
      }
    }
    if (failures.length === 0) {
      onDeleted(players.length);
      return;
    }
    const removed = players.length - failures.length;
    setError(
      removed === 0
        ? failures.join(' · ')
        : `${removed} removed. ${failures.length} could not be deleted — ${failures.join(' · ')}`
    );
  }

  return (
    <ConfirmDialog
      title={players.length === 1 ? 'Delete Player' : 'Delete Selected Players'}
      tone="danger"
      confirmLabel={players.length === 1 ? 'Delete' : `Delete ${players.length}`}
      error={error}
      consequence={`${describe(players)} They will no longer appear on play entry, participation or export lists. Plays already logged against them stay in the archive.`}
      onCancel={onCancel}
      onConfirm={confirm}
    />
  );
}
